/**
 * 비밀번호 재설정 페이지
 */

import React, { useState } from 'react';
import { Form, Input, Button, Card, Typography, Alert, Space, Result } from 'antd';
import { MailOutlined } from '@ant-design/icons';
import { Link, useNavigate } from 'react-router-dom';
import authService from '../../services/authService';
import { getErrorMessage } from '../../utils/api';
import './AuthPages.css';

const { Title, Text } = Typography;

/**
 * 비밀번호 재설정 페이지 컴포넌트
 */
export const ResetPasswordPage: React.FC = () => {
  const navigate = useNavigate();
  const [form] = Form.useForm();
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [isSuccess, setIsSuccess] = useState(false);

  /**
   * 비밀번호 재설정 요청 제출
   */
  const handleSubmit = async (values: { email: string }) => {
    try {
      setIsLoading(true);
      setError(null);

      await authService.requestPasswordReset(values);

      setIsSuccess(true);
    } catch (err: any) {
      const errorMessage = getErrorMessage(err);
      setError(errorMessage);
    } finally {
      setIsLoading(false);
    }
  };

  // 성공 화면
  if (isSuccess) {
    return (
      <div className="auth-container">
        <Card className="auth-card" variant="borderless">
          <Result
            status="success"
            title="비밀번호 재설정 이메일 발송 완료"
            subTitle="입력하신 이메일로 비밀번호 재설정 링크를 발송했습니다. 이메일을 확인해주세요."
            extra={[
              <Link to="/login" key="login">
                <Button type="primary">로그인 페이지로</Button>
              </Link>,
            ]}
          />
        </Card>
      </div>
    );
  }

  // 입력 화면
  return (
    <div className="auth-container">
      <Card className="auth-card" variant="borderless">
        <Space direction="vertical" size="large" style={{ width: '100%' }}>
          {/* 로고 및 타이틀 - 로고 클릭 시 랜딩페이지로 이동 */}
          <div className="auth-header">
            <Title
              level={3}
              style={{
                margin: 0,
                marginBottom: '8px',
                textAlign: 'center',
                cursor: 'pointer',
              }}
              onClick={() => navigate('/')}
            >
              📚 채점톡
            </Title>
            <Title level={4} style={{ margin: 0, textAlign: 'center' }}>
              비밀번호 재설정
            </Title>
            <Text type="secondary" style={{ textAlign: 'center', display: 'block' }}>
              가입하신 이메일을 입력해주세요
            </Text>
          </div>

          {/* 에러 메시지 */}
          {error && (
            <Alert message="오류" description={error} type="error" showIcon closable />
          )}

          {/* 안내 메시지 */}
          <Alert
            message="비밀번호 재설정 안내"
            description="입력하신 이메일로 비밀번호 재설정 링크를 발송해드립니다. 이메일의 링크를 클릭하여 새로운 비밀번호를 설정해주세요."
            type="info"
            showIcon
          />

          {/* 이메일 입력 폼 */}
          <Form
            form={form}
            name="reset-password"
            onFinish={handleSubmit}
            autoComplete="off"
            size="large"
            layout="vertical"
          >
            <Form.Item
              name="email"
              rules={[
                { required: true, message: '이메일을 입력해주세요' },
                { type: 'email', message: '올바른 이메일 형식이 아닙니다' },
              ]}
            >
              <Input
                prefix={<MailOutlined />}
                placeholder="이메일"
                autoComplete="email"
              />
            </Form.Item>

            <Form.Item>
              <Button
                type="primary"
                htmlType="submit"
                loading={isLoading}
                block
                style={{ marginTop: '8px' }}
              >
                재설정 링크 발송
              </Button>
            </Form.Item>
          </Form>

          {/* 하단 링크 */}
          <div className="auth-footer">
            <Space split="|" size="middle">
              <Link to="/login">
                <Text>로그인</Text>
              </Link>
              <Link to="/signup">
                <Text>회원가입</Text>
              </Link>
            </Space>
          </div>
        </Space>
      </Card>
    </div>
  );
};

export default ResetPasswordPage;
